import * as Sentry from "@sentry/react";
import config from "config";
import React, {
  memo,
  PureComponent,
  ReactNode,
  useCallback,
  useEffect,
} from "react";

// Turning sentry off except in QA, it's too noisy with minimized errors coming
// from outside our code base. We need to figure out what to filter and what to
// fix before we put it back in prod.
const useSentry =
  config.get("sentryEnvironment") === "qa" && config.get("sentryFrontendDSN");

type ErrorBoundaryProps = {
  children: ReactNode;
  onError: (error: Error, errorInfo: React.ErrorInfo) => void;
  showError: boolean;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

class ErrorBoundary extends PureComponent<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const { onError } = this.props;

    onError(error, errorInfo);
    this.setState({ hasError: true });
  }

  render(): ReactNode {
    const { children, showError } = this.props;
    const { hasError } = this.state;

    return hasError ? (
      showError ? (
        <div>
          <p>
            Minted will be back shortly. We are working hard to make our site
            available to you as soon as possible. Please check back in a few
            minutes. Have a question? Contact us at 1.888.828.6468 or
            help@minted.com.
          </p>
        </div>
      ) : null
    ) : (
      children
    );
  }
}

export type SentryBoundaryProps = {
  children: ReactNode;
  showError?: boolean;
};

const SentryBoundary = ({
  children,
  showError = true,
}: SentryBoundaryProps) => {
  useEffect(() => {
    if (useSentry) {
      Sentry.init({
        dsn: config.get("sentryFrontendDSN") as string,
        environment: `${config.get("sentryEnvironment")}-client`,
        release: config.get("deployedCommit") as string,
        sampleRate: parseFloat(`${config.get("sentrySampleRate")}`),
      });
    }
  }, []);

  const handleError = useCallback(
    (error: Error, errorInfo: React.ErrorInfo) => {
      if (useSentry) {
        Sentry.captureException(error, { extra: { errorInfo } });
      } else {
        console.error("Error boundary: ", error, errorInfo);
      }
    },
    [],
  );

  return (
    <ErrorBoundary onError={handleError} showError={showError}>
      {children}
    </ErrorBoundary>
  );
};

const MemoizedSentryBoundary = memo(SentryBoundary);

export { MemoizedSentryBoundary as SentryBoundary };
